import React from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {Table, Tag} from "antd";
import moment from "moment";
import {
    RequestsTableContainer,
    TableTitle,
    ActionButton
} from "../style/CustomerRequestsPage.styles";
import {EyeOutlined} from "@ant-design/icons";
import RequestTableFilters from "./RequestTableFilters";

const RequestsDataTable = ({
    title,
    data = [],
    loading = false,
    onViewDetails,
    pagination = null,
    onPaginationChange,
    filters = {},
    handleUpdateFilters = () => {}
}) => {
    const {t} = useTranslation();
    const isMobile = window.innerWidth <= 768;

    const getStatusText = status => {
        const statusMap = {
            all: t("screens.customerRequests.all"),
            waiting: t("screens.customerRequests.waiting"),
            pending_approval: t("screens.customerRequests.pendingApproval"),
            recruitment_in_progress: t("screens.customerRequests.recruitmentInProgress"),
            recruitment_completed: t("screens.customerRequests.recruitmentCompleted"),
            completed: t("screens.customerRequests.completed"),
            cancelled: t("screens.customerRequests.cancelled"),
            rejected: t("screens.customerRequests.rejected"),
            approved: t("screens.customerRequests.approved")
        };
        return statusMap[status] || status;
    };

    const getStatusColor = status => {
        const colorMap = {
            all: "blue",
            waiting: "#808080",
            pending_approval: "#faad14",
            recruitment_in_progress: "#1890ff",
            recruitment_completed: "#722ed1",
            completed: "#52c41a",
            cancelled: "#d9d9d9",
            rejected: "#ff4d4f",
            approved: "#52c41a"
        };
        return colorMap[status] || "default";
    };

    const getRequestTypeText = (type, subType) => {
        if (type === "support") return t("screens.customerRequests.support");
        if (type === "planning") {
            return subType === "increment"
                ? t("screens.customerRequests.increase")
                : t("screens.customerRequests.decrease");
        }
        return t("screens.customerRequests.planning");
    };

    const getTotalHours = (startHour, endHour) => {
        if (!startHour || !endHour) return 0;
        const start = parseInt(startHour.split(":")[0]);
        const end = parseInt(endHour.split(":")[0]);
        if (end > start) {
            return end - start;
        } else if (end < start) {
            return 24 - start + end;
        }
        return 0;
    };

    const getTotalDays = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        const start = moment(startDate).startOf("day");
        const end = moment(endDate).startOf("day");
        return end.diff(start, "days") + 1;
    };

    const formatElapsedTime = createdAt => {
        if (!createdAt) return "-";
        const createdMoment = moment(createdAt);
        if (!createdMoment.isValid()) return "-";

        const now = moment();
        const duration = moment.duration(now.diff(createdMoment));
        const days = Math.max(0, Math.floor(duration.asDays()));

        return `${days}`;
    };

    const columns = [
        {
            title: t("screens.customerRequests.requestDate"),
            dataIndex: "createdAt",
            key: "createdAt",
            width: isMobile ? "auto" : 150,
            render: text => moment(text).format("DD.MM.YY HH:mm")
        },
        {
            title: t("screens.customerRequests.subcompanyName"),
            dataIndex: "subcompanyName",
            key: "subcompanyName",
            width: isMobile ? "auto" : 150,
            render: text => text || "-"
        },
        {
            title: t("screens.customerRequests.requestType"),
            dataIndex: "type",
            key: "type",
            width: isMobile ? "auto" : 120,
            render: (text, record) => getRequestTypeText(text, record.subType)
        },
        {
            title: t("screens.customerRequests.status"),
            dataIndex: "status",
            key: "status",
            width: isMobile ? "auto" : 130,
            render: status => <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
        },
        {
            title: t("screens.customerRequests.hour"),
            key: "hours",
            width: isMobile ? "auto" : 80,
            render: (_, record) => getTotalHours(record.startHour, record.endHour),
            sorter: (a, b) => {
                const aHours = getTotalHours(a.startHour, a.endHour);
                const bHours = getTotalHours(b.startHour, b.endHour);
                return aHours - bHours;
            }
        },
        {
            title: t("screens.customerRequests.day"),
            key: "days",
            width: isMobile ? "auto" : 80,
            render: (_, record) => getTotalDays(record.startDate, record.endDate)
        },
        {
            title: t("screens.customerRequests.courierCount"),
            dataIndex: "courierCount",
            key: "courierCount",
            width: isMobile ? "auto" : 120
        },
        {
            title: t("screens.customerRequests.packageCount"),
            dataIndex: "packageCount",
            key: "packageCount",
            width: isMobile ? "auto" : 120,
            render: text => text || "-"
        },
        {
            title: t("screens.customerRequests.actions"),
            key: "actions",
            width: isMobile ? "auto" : 120,
            render: (_, record) => (
                <ActionButton
                    type="default"
                    icon={<EyeOutlined />}
                    onClick={() => onViewDetails(record)}
                >
                    {t("screens.customerRequests.view")}
                </ActionButton>
            )
        },
        {
            title: t("screens.customerRequests.ellapsedTime"),
            dataIndex: "createdAt",
            width: isMobile ? "auto" : 120,
            render: createdAt => <div>{formatElapsedTime(createdAt)} gün</div>
        }
    ];

    return (
        <RequestsTableContainer>
            <RequestTableFilters filters={filters} handleUpdateFilters={handleUpdateFilters} />
            <TableTitle>{title}</TableTitle>
            <Table
                dataSource={data}
                columns={columns}
                loading={loading}
                pagination={
                    pagination
                        ? {
                              current: pagination.page,
                              pageSize: pagination.limit,
                              total: pagination.total,
                              showTotal: (total, range) => `${total} kayıt`,
                              onChange: onPaginationChange
                          }
                        : false
                }
                locale={{
                    emptyText: t("screens.customerRequests.noRequestsFound")
                }}
                scroll={{x: "max-content"}}
                rowKey="_id"
            />
        </RequestsTableContainer>
    );
};

export default observer(RequestsDataTable);
